// The rows the cable worksheets print. The panel derives its metres from the
// run's geometry plus the estimator's stored edits; the export has to derive
// them the same way, from the same applyOverrides, or the spreadsheet contradicts
// the screen it was exported from.

import {
  applyOverrides,
  hasAnyOverride,
  type DerivedCableTakeoff,
} from "../../CableTakeoffTabV3/cableEditGeometry";
import type { CableBoqItem } from "../../utils/fetchCables";
import { appCircuitRows } from "../../CableTakeoffTabV3/appCircuits";
import { cableTypeSplitsOf } from "../../CableTakeoffTabV3/circuitLegs";
import { FEEDER_CABLE_SUGGESTION } from "../../CableTakeoffTabV3/feederCircuits";
import type { DeviceCategory } from "../../CableTakeoffTabV3/deviceCategories";
import type {
  BuildCableExportRows,
  CableExportPlan,
  CableExportRow,
  CableExportRowStatus,
} from "./types";

/** Below this a difference is re-measurement noise, not an edit. */
const EDIT_EPSILON_M = 0.05;

const circuitLabel = (item: CableBoqItem): string =>
  item.text || item.circuit || "";

const publishedRow = (
  plan: CableExportPlan,
  item: CableBoqItem,
  status: CableExportRowStatus,
): CableExportRow => ({
  planName: plan.planName,
  board: item.board ?? "",
  circuit: circuitLabel(item),
  cableType: item.cable_type ?? "",
  system: item.system,
  method: item.method ?? "",
  deviceCount: item.device_count ?? 0,
  measuredM: null,
  verticalM: null,
  lengthM: Number(item.quantity) || 0,
  status,
});

interface DerivedRowArgs {
  plan: CableExportPlan;
  circuitId: string;
  derived: DerivedCableTakeoff;
  baseline: DerivedCableTakeoff | null;
  struck: boolean;
  item?: CableBoqItem;
}

const derivedRow = ({
  plan,
  circuitId,
  derived,
  baseline,
  struck,
  item,
}: DerivedRowArgs): CableExportRow[] => {
  const lengthM = derived.lengthsByCircuit[circuitId] ?? 0;
  const derivedDeviceCount =
    derived.geometry.byCircuit[circuitId]?.paths.length ?? 0;
  const baselineLengthM = baseline?.lengthsByCircuit[circuitId] ?? 0;
  const baselineDeviceCount =
    baseline?.geometry.byCircuit[circuitId]?.paths.length ?? 0;
  const isEdited =
    baseline !== null &&
    (Math.abs(lengthM - baselineLengthM) > EDIT_EPSILON_M ||
      derivedDeviceCount !== baselineDeviceCount);

  // A feeder is no BoQ row of the run's: it is named after the board it feeds, from the board upstream.
  const feeder = derived.feederCircuits[circuitId];
  const boardNameOf = (label: string) =>
    derived.geometry.sources.find((source) => source.label === label)?.name ?? label;
  const row: CableExportRow = {
    planName: plan.planName,
    board: feeder ? boardNameOf(feeder.fromLabel) : (item?.board ?? ""),
    circuit: feeder ? `Zuleitung ${boardNameOf(feeder.toLabel)}` : item ? circuitLabel(item) : circuitId,
    cableType:
      derived.cableTypeByCircuit[circuitId] ??
      item?.cable_type ??
      (feeder ? FEEDER_CABLE_SUGGESTION : ""),
    system: item?.system,
    method: item?.method ?? "",
    // A struck circuit has no routes left, so its devices are counted from what
    // was published — they are still drawn, and still have to end up somewhere.
    deviceCount: struck ? (item?.device_count ?? 0) : derivedDeviceCount,
    measuredM: derived.measuredByCircuit[circuitId] ?? 0,
    verticalM: derived.verticalByCircuit[circuitId]?.verticalM ?? 0,
    lengthM,
    status: struck ? "struck" : isEdited ? "edited" : "unchanged",
  };
  // A run on two cable types is two lines in a bill of quantities, one per type.
  const shares = cableTypeSplitsOf({
    legsByCircuit: derived.legsByCircuit,
    cableTypeByLegByCircuit: derived.cableTypeByLegByCircuit,
    circuitTypeOf: () => row.cableType,
  })[circuitId];
  if (!shares) return [row];
  const legs = derived.legsByCircuit[circuitId] ?? {};
  return shares.map((share) => ({
    ...row,
    cableType: share.cableType,
    measuredM: share.kinds.reduce((sum, kind) => sum + (legs[kind]?.horizontalM ?? 0), 0),
    verticalM: share.kinds.reduce((sum, kind) => sum + (legs[kind]?.verticalM ?? 0), 0),
    lengthM: share.totalM,
  }));
};

const planRows = (
  plan: CableExportPlan,
  categoryName: (category: DeviceCategory) => string,
): CableExportRow[] => {
  const items = plan.items ?? [];
  const hasEdits = hasAnyOverride(plan.overrides);

  if (!plan.geometry) {
    const status: CableExportRowStatus = hasEdits
      ? "editsUnavailable"
      : "unchanged";
    return items.map((item) => publishedRow(plan, item, status));
  }

  const derived = applyOverrides(
    plan.geometry,
    plan.overrides,
    plan.imageScaleFactor,
    plan.scalePxPerM,
    {},
    { cache: plan.routes ?? {} },
  );
  // "Edited" has to mean the estimator changed it, not that the browser
  // re-measured the geometry a few centimetres away from the run.
  const baseline = hasEdits
    ? applyOverrides(plan.geometry, {}, plan.imageScaleFactor, plan.scalePxPerM)
    : null;
  const struckCircuitIds = new Set(derived.orphanedDevices.circuitIds);

  const takenCircuitIds = new Set<string>();
  const rows = items.flatMap((item) => {
    const circuitId = item.circuit;
    if (!circuitId || derived.lengthsByCircuit[circuitId] === undefined) {
      return [publishedRow(plan, item, "unchanged")];
    }
    takenCircuitIds.add(circuitId);
    return derivedRow({
      plan,
      circuitId,
      derived,
      baseline,
      struck: struckCircuitIds.has(circuitId),
      item,
    });
  });

  // Circuits the geometry carries but the BoQ never listed still cost cable, so
  // dropping them here would take metres off the total the panel shows.
  const appItems = appCircuitRows({ circuits: derived.appCircuits, categoryName });
  Object.entries(derived.lengthsByCircuit).forEach(([circuitId, lengthM]) => {
    if (takenCircuitIds.has(circuitId) || lengthM <= 0) return;
    rows.push(
      ...derivedRow({
        plan,
        circuitId,
        derived,
        baseline,
        struck: struckCircuitIds.has(circuitId),
        item: appItems.find((item) => item.circuit === circuitId),
      }),
    );
  });

  return rows;
};

export const buildCableExportRows: BuildCableExportRows = ({
  plans,
  categoryName = (category) => category,
}) => {
  const rows = plans.flatMap((plan) => planRows(plan, categoryName));
  const totalM = rows.reduce((sum, row) => sum + row.lengthM, 0);
  return { rows, totalM };
};
